import { GetApplicationDataResponse } from 'handlers/applicationData';

import Api, { ErrorMode, FetchMethodType } from './Api';

export interface PlaidRequestParams {
  applicationId: string;
  plaidPublicToken: string;
  userConfirmedAccountOwnership?: boolean;
  runIncomeVerification?: boolean;
}

export interface PlaidLinkToken {
  LINK_TOKEN: string;
}

export interface BankAccountInfo {
  id: string;
  name: string;
  officialName: string | null;
  mask: string;
  balance: number;
}

export interface ExchangePublicTokenResponse {
  accountId: string;
  bankName: string;
  accountName: string;
  accountMask: string;
  isSavingsAccount: boolean;
}

export interface SavingsAccount {
  id: string;
  name: string;
  mask: string;
}

export interface GetSavingsAccountsResponse {
  savingsAccounts: SavingsAccount[];
}

export interface ValidateIdentityParams {
  applicationId: string;
  userConfirmedAccountOwnership?: boolean;
}

export interface PlaidApi {
  getLinkToken(payload: { applicationId: string; skipIncomeVerification?: boolean }): Promise<PlaidLinkToken>;
  sendPlaidSuccess(appId: string, params: PlaidRequestParams): Promise<void>;
  validateIdentity(params: ValidateIdentityParams): Promise<GetApplicationDataResponse>;
  getCheckingAccounts(appId: string): Promise<BankAccountInfo[]>;
  exchangePublicToken(appId: string, plaidPublicToken: string): Promise<ExchangePublicTokenResponse>;
  getSavingsAccounts(appId: string): Promise<GetSavingsAccountsResponse>;
}

export enum SetBankAccountsErrorType {
  AccountOwnerMismatchError = 'AccountOwnerMismatchError',
}

export enum PlaidAccountError {
  ACCOUNT_OWNER_MISMATCH_ERROR = 'ACCOUNT_OWNER_MISMATCH_ERROR',
  OTHER = 'OTHER',
}

export type SetBankAccountsError = {
  status: number;
  error: string;
  errorType: SetBankAccountsErrorType;
};

export default class PlaidRestApi extends Api implements PlaidApi {
  public async getLinkToken({
    applicationId,
    skipIncomeVerification,
  }: {
    applicationId: string;
    skipIncomeVerification?: boolean;
  }) {
    const response = await this.fetch<PlaidLinkToken>(`/plaid/linkToken`, {
      method: FetchMethodType.POST,
      body: {
        applicationId,
        skipIncomeVerification: !!skipIncomeVerification,
      },
    });
    return response;
  }

  public async sendPlaidSuccess(appId: string, body: PlaidRequestParams) {
    await this.fetch(`/plaid/handlePlaidSuccess/${appId}`, {
      errorMode: ErrorMode.HideError,
      method: FetchMethodType.POST,
      body,
    });
  }

  public async validateIdentity({ applicationId, userConfirmedAccountOwnership }: ValidateIdentityParams) {
    const response = await this.fetch<GetApplicationDataResponse>(`/plaid/validateIdentity/${applicationId}`, {
      errorMode: ErrorMode.HideError,
      method: FetchMethodType.POST,
      body: { userConfirmedAccountOwnership },
    });
    return response;
  }

  public async getCheckingAccounts(appId: string) {
    const response = await this.fetch<BankAccountInfo[]>(`/plaid/bankAccounts/${appId}`, {
      method: FetchMethodType.GET,
    });
    return response;
  }

  public async exchangePublicToken(appId: string, plaidPublicToken: string) {
    const response = await this.fetch<ExchangePublicTokenResponse>(`/plaid/exchangePublicToken/${appId}`, {
      method: FetchMethodType.POST,
      body: { plaidPublicToken },
    });
    return response;
  }

  public async getSavingsAccounts(appId: string): Promise<GetSavingsAccountsResponse> {
    return this.fetch<GetSavingsAccountsResponse>(`/plaid/savingsAccounts/${appId}`, {
      method: FetchMethodType.GET,
    });
  }
}
